/**
 * CallComponent.js
 * Talkroute
 *
 * Created by Vel Genov on 3/08/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import ReactTimeout from "react-timeout";
import { connect } from "react-redux";
import { Confirm } from "semantic-ui-react";
import { withToastManager } from "react-toast-notifications";
import { UserAgent, Registerer, Invitation, Inviter } from "sip.js";

import {
  idleCall,
  callConnecting,
  callConnected,
  incomingCall,
  setOutgoingPhoneNumber,
  setCallSession,
  accept,
} from "../../actions/callActions";
import { DEFAULT_AUDIO_SOURCE } from "../../actions/preferencesActions";
import uiString from "../../constants/uiString";
import { formatPhoneNumber } from "../../services/dataClasses/CallData";
import { CallStateEnum } from "../../constants/callConstants";
import DataState from "../../constants/dataStateConstants";
import ConnectingLoader from "./ConnectingLoader";
import EndingCallLoader from "./EndingCallLoader";
import ConnectingCallLoader from "./ConnectingCallLoader";
import routes from "../../constants/routes";

import * as sipLifecycle from "./helpers/sip-lifecycle";
import * as stateUtils from "./helpers/state-utils";

export interface ICallComponentProps extends ReactTimeout.ReactTimeoutProps {
  numberToCall: string;
  preferencesFromPhoneNumber: string;
  callReducerFromPhoneNumber: string;
  callState: CallStateEnum;
  incomingCallNumber: string;
  defaultAudioInput: string;
  defaultAudioOutput: string;
  connectionDataState: DataState;
  webrtcUserName: string;
  webrtcPassword: string;
  userToken: string;
  impu: string;
  webSocketProxyUrl: string;
  toastManager: any;
  isMuteMic: boolean;
  isCallHold: boolean;
  dtmfString: string;
  callNumber: string;
  defaultAudioOutputVolume: number;
  defaultAudioInputVolume: number;
  defaultOutgoingPhoneNumber: string;
  outgoingPhoneNumber: string;
  // redux actions
  callConnected: (number: string) => any;
  callConnecting: () => any;
  idleCall: () => any;
  incomingCall: (sanitizedNumber: number, dialedNumber: number) => any;
  setOutgoingPhoneNumber: (number: string) => any;
  setCallSession: (callSession?: Invitation | Inviter) => any;
  activeRoute: string;
}

export interface ICallComponentState {
  sipAgent: UserAgent | null;
  sipRegisterer: Registerer | null;
  callSession: Invitation | Inviter | null;
  isConnecting: boolean;
  isSipConnected: boolean;
  isSipRegistered: boolean;
  isEndingCall: boolean;
  isShowConnecting: boolean;
  reconnecting: boolean;
}

export interface ICallComponent {
  updateMicrophoneVolume: () => any;
  showIncomingCallScreen: (fromPhoneNumber: string) => any;
  showToast: (message: string, persist?: boolean, disableDismiss?: boolean) => any;
  removeToasts: () => any;
  updateOutgoingPhoneNumber: (newPhoneNumber: string) => any;
  retrySipTimer?: ReactTimeout.Timer;
  checkSipTimer?: ReactTimeout.Timer;
  endCallTimer?: ReactTimeout.Timer;
  retryCreateSipTimer?: ReactTimeout.Timer;
  _ismounted: boolean;
}

export type CallComponentInstance = React.Component<
  ICallComponentProps,
  ICallComponentState
> &
  ICallComponent;

/**
 * CallComponent component stub
 */
class CallComponent
  extends React.Component<ICallComponentProps, ICallComponentState>
  implements ICallComponent {
  retrySipTimer: ReactTimeout.Timer | undefined;
  checkSipTimer: ReactTimeout.Timer | undefined;
  endCallTimer: ReactTimeout.Timer | undefined;
  retryCreateSipTimer: ReactTimeout.Timer | undefined;
  _ismounted = false;

  public static defaultProps = {
    connectionDataState: DataState.LOADING,
    defaultAudioInput: DEFAULT_AUDIO_SOURCE,
    defaultAudioOutput: DEFAULT_AUDIO_SOURCE,
  };
  /**
   * Show a screen prompting to accept an incoming call
   *
   * @param fromPhoneNumber the incoming phone number
   */
  showIncomingCallScreen = (fromPhoneNumber) => {
    const formattedIncomingNumber = formatPhoneNumber(fromPhoneNumber);
    const content = `${uiString.CALL_FROM} ${formattedIncomingNumber}. ${uiString.ACCEPT}?`;
    const open = true;
    return (
      <Confirm
        content={content}
        open={open}
        onConfirm={() => sipLifecycle.sipjsAcceptCall(this)}
      />
    );
  };

  /**
   * Show a toast message
   *
   * @param message the message to display
   */
  showToast = (message, persist?: boolean, disableDismiss?: boolean) => {
    const { toastManager } = this.props;

    toastManager.add(<div>{message}</div>, {
      appearance: "info",
      autoDismiss: !persist,
      pauseOnHover: false,
      disableDismiss
    });
  };

  /**
   * Remove all toast messages
   *
   */
  removeToasts = () => {
    const { toastManager } = this.props;
    toastManager.removeAll();
    this.setState({reconnecting: false});
  };

  /**
   * Update the outgoing phone number
   *
   * @param newPhoneNumber the phone number to update with
   */
  updateOutgoingPhoneNumber = (newPhoneNumber) => {
    const { setOutgoingPhoneNumber: setOutgoingPhoneNumberLocal } = this.props;

    if (newPhoneNumber) {
      setOutgoingPhoneNumberLocal(newPhoneNumber);
    }
  };

  /**
   * Update microphone volume
   *
   */
  updateMicrophoneVolume = () => {
    const {
      defaultAudioInput,
      defaultAudioInputVolume,
      isMuteMic,
    } = this.props;
    const { callSession } = this.state;
    const constraints = {
      audio: {
        deviceId: defaultAudioInput ? { exact: defaultAudioInput } : undefined,
      },
    };

    let mediaStream: MediaStream | null = null;
    let audioSource: MediaStreamAudioSourceNode | null = null;
    let audioContext: AudioContext | null = null;

    // set custom input volume for microphone
    navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
      mediaStream = stream;
      (callSession?.sessionDescriptionHandler as any)?.peerConnection
        .getSenders()
        .forEach((sender) => {
          audioContext = new AudioContext();
          const gainNode = audioContext.createGain();
          audioSource = audioContext.createMediaStreamSource(stream);
          const audioDestination = audioContext.createMediaStreamDestination();
          audioSource.connect(gainNode);
          gainNode.connect(audioDestination);
          gainNode.gain.value = defaultAudioInputVolume;
          audioDestination.stream.getTracks()[0].enabled = !isMuteMic;
        });
    });

    return () => {
      if (audioContext) {
        audioContext.close();
        audioContext = null;
      }
  
      if (audioSource) {
        audioSource.disconnect();
        audioSource = null;
      }
  
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop);
        mediaStream = null;
      }
    };
  };

  constructor(props) {
    super(props);

    this.state = {
      isSipConnected: true,
      isConnecting: false,
      isSipRegistered: true,
      isEndingCall: false,
      isShowConnecting: false,
      callSession: null,
      sipAgent: null,
      sipRegisterer: null,
      reconnecting: false
    };
  }

  /**
   * Check if the number to call changed when the props get updated
   *
   * @param {*} prevProps the previous props
   */
  componentDidMount() {
    this._ismounted = true;
    const { connectionDataState, preferencesFromPhoneNumber } = this.props;
    if (connectionDataState === DataState.READY) {
      sipLifecycle.initSipJS(this);
    }

    if (preferencesFromPhoneNumber) {
      this.updateOutgoingPhoneNumber(preferencesFromPhoneNumber);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      connectionDataState: prevConnectionDataState,
      callState: prevCallState,
      preferencesFromPhoneNumber: prevPreferencesFromPhoneNumber,
      isMuteMic: prevIsMuteMic,
      isCallHold: prevIsCallHold,
      dtmfString: prevDtmfString,
      defaultAudioInput: prevDefaultAudioInput,
      defaultAudioOutput: prevDefaultAudioOutput,
      defaultAudioOutputVolume: prevDefaultAudioOutputVolume,
      defaultAudioInputVolume: prevDefaultAudioInputVolume,
    } = prevProps;
    const {
      connectionDataState,
      callState,
      preferencesFromPhoneNumber,
      isMuteMic,
      isCallHold,
      dtmfString,
      defaultAudioInput,
      defaultAudioOutput,
      defaultAudioOutputVolume,
      defaultAudioInputVolume,
    } = this.props;
    const { isSipRegistered } = this.state;

    // Initialize SipJS
    if (
      connectionDataState === DataState.READY &&
      prevConnectionDataState !== connectionDataState
    ) {
      sipLifecycle.initSipJS(this);
    }

    if (callState !== prevCallState) {
      sipLifecycle.processCallState(this);
    }

    if (prevCallState === CallStateEnum.CONNECTED && callState === CallStateEnum.IDLE) {
      sipLifecycle.processEndingCall(this);
    }

    if (
      prevCallState === CallStateEnum.INCOMING &&
      callState === CallStateEnum.ACCEPT
    ) {
      stateUtils.setIsShowConnecting(this, true);
    }

    if (prevCallState === CallStateEnum.ACCEPT && callState !== CallStateEnum.ACCEPT) {
      stateUtils.setIsShowConnecting(this, false);
    }

    if (prevPreferencesFromPhoneNumber !== preferencesFromPhoneNumber) {
      this.updateOutgoingPhoneNumber(preferencesFromPhoneNumber);
    }

    if (prevIsMuteMic !== isMuteMic) {
      sipLifecycle.setMicMute(this, isMuteMic);
    }

    if (callState === CallStateEnum.CONNECTED && prevIsCallHold !== isCallHold) {
      sipLifecycle.setCallHold(this, isCallHold);
    }

    // Check for updates in the DTMF string
    // Send a DTMF tone with the last character, if there is a change
    if (dtmfString && dtmfString !== prevDtmfString) {
      const lastCharacter = dtmfString.slice(-1);
      sipLifecycle.sendDTMF(this, lastCharacter);
    }

    // Check if Sip is registered
    if (isSipRegistered) {
      sipLifecycle.checkSipRegistered(this);
    }
    // Change input device in case it was updated
    if (defaultAudioInput !== prevDefaultAudioInput) {
      const constraints = {
        audio: {
          deviceId: defaultAudioInput
            ? { exact: defaultAudioInput }
            : undefined,
        },
      };
      navigator.mediaDevices.getUserMedia(constraints);
    }
    // Change output device in case it was updated
    if (defaultAudioOutput !== prevDefaultAudioOutput) {
      const audioPlayerElement = document.getElementById("audio-remote");
      (audioPlayerElement as any).setSinkId(defaultAudioOutput);
    }
    if (defaultAudioOutputVolume !== prevDefaultAudioOutputVolume) {
      const audioPlayerElement = document.getElementById("audio-remote");
      (audioPlayerElement as any).volume = defaultAudioOutputVolume;
    }
    if (defaultAudioInputVolume !== prevDefaultAudioInputVolume) {
      this.updateMicrophoneVolume();
    }
  }

  componentWillUnmount() {
    sipLifecycle.deinitSipJS(this);

    if (this.retrySipTimer) {
      this.props.clearTimeout!(this.retrySipTimer);
    }

    if (this.checkSipTimer) {
      this.props.clearTimeout!(this.checkSipTimer);
    }

    if (this.endCallTimer) {
      this.props.clearTimeout!(this.endCallTimer);
    }

    if (this.retryCreateSipTimer) {
      this.props.clearTimeout!(this.retryCreateSipTimer);
      this.retryCreateSipTimer = undefined;
    }
    this._ismounted = false;
  }

  render() {
    const { isSipConnected, isEndingCall, isShowConnecting } = this.state;
    const { activeRoute } = this.props;

    if (!isSipConnected && activeRoute === routes.HOME) {
      return <ConnectingLoader />;
    }

    if (isEndingCall) {
      return <EndingCallLoader />;
    }

    if (isShowConnecting) {
      return <ConnectingCallLoader />;
    }

    return null;
  }
}

// Redux data binding
const mapStateToProps = (state) => ({
  numberToCall: state.callReducer.numberToCall,
  callNumber: state.callReducer.callNumber,
  callState: state.callReducer.callState,
  incomingCallNumber: state.callReducer.incomingCallNumber,
  defaultOutgoingPhoneNumber: state.callReducer.defaultOutgoingPhoneNumber,
  outgoingPhoneNumber: state.callReducer.outgoingPhoneNumber,
  preferencesFromPhoneNumber: state.preferences.selectedOutgoingPhoneNumber,
  impu: state.webRTCConnection.impu,
  webSocketProxyUrl: state.webRTCConnection.webSocketProxyUrl,
  connectionDataState: state.webRTCConnection.dataState,
  webrtcUserName: state.authentication.webrtcUserName,
  webrtcPassword: state.authentication.webrtcPassword,
  isMuteMic: state.callReducer.isMuteMic,
  isCallHold: state.callReducer.isCallHold,
  dtmfString: state.callReducer.dtmfString,
  defaultAudioInput: state.preferences.defaultAudioInput,
  defaultAudioOutput: state.preferences.defaultAudioOutput,
  userToken: state.authentication.accessToken,
  defaultAudioOutputVolume: state.preferences.defaultAudioOutputVolume,
  defaultAudioInputVolume: state.preferences.defaultAudioInputVolume,
  activeRoute: state.router.location.pathname
});

export default withToastManager(
  connect(mapStateToProps, {
    accept,
    idleCall,
    callConnecting,
    callConnected,
    incomingCall,
    setOutgoingPhoneNumber,
    setCallSession,
  })(ReactTimeout(CallComponent as any))
);
